import React, { useMemo } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import theme from "src/v2/theme/theme"
import { formatCpfCnpj } from "src/v2/utils/formatter/format-cpf-cnpj"
import { FIELD_TYPE } from "./company-registration-config"

const isEmpty = (value) => value === null || value === undefined || value === ""

const getSafeExternalUrl = (value) => {
    try {
        const url = new URL(value)

        return ["http:", "https:"].includes(url.protocol) ? url.href : null
    } catch {
        return null
    }
}

const formatDetailValue = (field, row) => {
    const value = field.detailValue ? field.detailValue(row[field.name], row) : row[field.name]

    if (isEmpty(value)) return null
    if (field.type === FIELD_TYPE.DATE) return dayjs(value).format("DD/MM/YYYY")
    if (field.type === FIELD_TYPE.BOOLEAN) {
        if (value === true) return "Sim"
        if (value === false) return "Não"
        return null
    }
    if (["CPFCNPJ", "CNPJAuditor"].includes(field.name)) return formatCpfCnpj(value)

    return value
}

const CompanyRegistrationDetails = ({ open, definition, row, onClose }) => {
    const sections = useMemo(() => definition.fields.reduce((groups, field) => {
        if (field.visibleInDetails === false) return groups
        if (field.visibleInDetailsWhen && !field.visibleInDetailsWhen(row[field.name], row)) return groups

        const value = formatDetailValue(field, row)
        if (isEmpty(value)) return groups

        const externalUrl = field.detailLink ? getSafeExternalUrl(value) : null

        return {
            ...groups,
            [field.section]: [...(groups[field.section] ?? []), { ...field, value, externalUrl }],
        }
    }, {}), [definition.fields, row])
    const sectionEntries = Object.entries(sections)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, color: theme.palette.common.white, maxHeight: "calc(100dvh - 32px)" } }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, pr: 1.5 }}>
                <Typography component="span" variant="h3" color="common.white">{definition.title}</Typography>
                <IconButton aria-label={`Fechar detalhes de ${definition.title}`} onClick={onClose} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: theme.palette.transparent.white10, py: 2 }}>
                {sectionEntries.map(([section, fields], index) => (
                    <React.Fragment key={section}>
                        <Box component="section">
                            <Typography variant="subtitle1" sx={{ color: theme.palette.icon.white70, mb: 1.5 }}>{section}</Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xxxs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                                {fields.map((field) => (
                                    <Box
                                        key={field.name}
                                        data-field-name={field.name}
                                        sx={{ minWidth: 0, gridColumn: (field.fullWidthInDetails ?? field.fullWidth) ? "1 / -1" : undefined }}
                                    >
                                        <Typography variant="caption" sx={{ color: theme.palette.icon.white70 }}>{field.label}</Typography>
                                        {field.externalUrl ? (
                                            <Typography
                                                component="a"
                                                href={field.externalUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                color="common.white"
                                                sx={{ display: "block", overflowWrap: "anywhere" }}
                                            >
                                                Abrir {field.label}
                                            </Typography>
                                        ) : (
                                            <Typography color="common.white" sx={{ overflowWrap: "anywhere" }}>{field.value}</Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        {index < sectionEntries.length - 1 && <Divider sx={{ borderColor: theme.palette.transparent.white10, my: 3 }} />}
                    </React.Fragment>
                ))}
            </DialogContent>
        </Dialog>
    )
}

CompanyRegistrationDetails.propTypes = {
    open: PropTypes.bool.isRequired,
    definition: PropTypes.shape({
        title: PropTypes.string.isRequired,
        fields: PropTypes.array.isRequired,
    }).isRequired,
    row: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
}

export default CompanyRegistrationDetails
